import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link as ScrollLink } from "react-scroll";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../services/auth";

const NavBar = () => {
    const { isAuthenticated, isAdmin, setIsAuthenticated, setIsAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const isHomePage = location.pathname === "/";
    const menuRef = useRef(null);

    // Handle dark mode toggle
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
        localStorage.setItem("darkMode", darkMode);
    }, [darkMode]);

    // Handle logout
    const handleLogout = useCallback(() => {
        authService.logout();
        setIsAuthenticated(false);
        setIsAdmin(false);
        navigate("/");
    }, [navigate, setIsAuthenticated, setIsAdmin]);

    const sections = ["home", "about", "projects", "skills", "contact"];

    // Close mobile menu when clicking outside - use refs for direct DOM handling
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        }

        // Only add the event listener when the menu is open
        if (isMenuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMenuOpen]);

    // Direct scroll function that works reliably
    const directScrollToSection = useCallback((sectionId) => {
        try {
            const element = document.getElementById(sectionId);
            if (element) {
                const yOffset = -80; // Adjust based on your navbar height
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
                return true;
            }
        } catch (error) {
            console.error("Error scrolling to section:", error);
        }
        return false;
    }, []);

    // Handle section navigation
    const handleSectionClick = useCallback((section) => {
        if (isHomePage) {
            directScrollToSection(section);
        } else {
            navigate(`/?section=${section}`);
        }
        setIsMenuOpen(false);
    }, [isHomePage, navigate, directScrollToSection]);

    // Handle direct admin login navigation
    const handleDirectAdminLogin = useCallback(() => {
        navigate("/admin-login");
        setIsMenuOpen(false);
    }, [navigate]);

    // Handle direct admin chat navigation
    const handleDirectAdminChat = useCallback(() => {
        navigate("/admin-chat");
        setIsMenuOpen(false);
    }, [navigate]);

    // Enhanced smooth dark mode toggle
    const handleSmoothDarkModeToggle = useCallback(() => {
        const newMode = !darkMode;

        // Apply the class first for immediate visual feedback
        if (newMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }

        // Update localStorage
        localStorage.setItem("darkMode", newMode.toString());

        // Update React state
        setDarkMode(newMode);

        // Close the mobile menu after a small delay to allow for transition
        if (isMenuOpen) {
            // Wait for the theme transition to complete before closing the menu
            setTimeout(() => {
                setIsMenuOpen(false);
            }, 300); // Matches the transition duration
        }
    }, [darkMode, isMenuOpen]);

    // Toggle menu function
    const toggleMenu = useCallback(() => {
        setIsMenuOpen(prevState => !prevState);
    }, []);

    // Close menu function
    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    return (
        <nav className="fixed top-0 left-0 w-full bg-white dark:bg-gray-900 shadow-md z-50 transition-all duration-300">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex justify-between items-center h-16">

                    {/* Logo */}
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white transition-colors duration-300">My Portfolio</h1>

                    {/* Mobile Menu Toggle Button */}
                    <div className="md:hidden flex items-center">
                        <button
                            type="button"
                            onClick={toggleMenu}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none transition-colors duration-300 relative z-30"
                        >
                            <span className="sr-only">Open menu</span>
                            <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-6">
                        {sections.map((section) => (
                            <ScrollLink
                                key={section}
                                to={section}
                                smooth={true}
                                duration={500}
                                offset={-80}
                                spy={true}
                                exact="true"
                                className="cursor-pointer text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-300"
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </ScrollLink>
                        ))}

                        {!isAuthenticated ? (
                            <button
                                type="button"
                                onClick={() => navigate("/admin-login")}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300"
                            >
                                Admin Login
                            </button>
                        ) : (
                            <>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => navigate("/admin-chat")}
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-300"
                                    >
                                        Admin Chat
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-300"
                                >
                                    Logout
                                </button>
                            </>
                        )}

                        <button
                            type="button"
                            onClick={handleSmoothDarkModeToggle}
                            className="p-2 rounded-full bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors duration-300"
                            aria-label="Toggle dark mode"
                        >
                            {darkMode ? "🌙" : "☀️"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Background Overlay */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 ${isMenuOpen ? "block" : "hidden"} transition-opacity duration-300`}
                onClick={closeMenu}
            ></div>

            {/* Mobile Menu */}
            <div
                ref={menuRef}
                className={`fixed top-0 left-0 w-3/4 max-w-sm h-full bg-white dark:bg-gray-900 shadow-lg transform ${isMenuOpen ? "translate-x-0" : "-translate-x-full"} transition-transform duration-300 ease-in-out z-50`}
            >
                <div className="flex justify-between items-center p-4">
                    <h1 className="text-lg font-bold text-gray-800 dark:text-white transition-colors duration-300">My Portfolio</h1>
                    <button
                        type="button"
                        onClick={closeMenu}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white focus:outline-none transition-colors duration-300"
                    >
                        <span className="sr-only">Close menu</span>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Direct buttons for sections to avoid event listener issues */}
                    {sections.map((section) => (
                        <button
                            key={section}
                            type="button"
                            className="block w-full text-left px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-300"
                            onClick={() => {
                                if (isHomePage) {
                                    // Direct DOM manipulation for scrolling
                                    const element = document.getElementById(section);
                                    if (element) {
                                        const yOffset = -80;
                                        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                        window.scrollTo({ top: y, behavior: 'smooth' });
                                    }
                                } else {
                                    navigate(`/?section=${section}`);
                                }
                                setIsMenuOpen(false);
                            }}
                        >
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                        </button>
                    ))}

                    {!isAuthenticated ? (
                        <button
                            type="button"
                            className="block w-full text-left px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300"
                            onClick={handleDirectAdminLogin}
                        >
                            Admin Login
                        </button>
                    ) : (
                        <>
                            {isAdmin && (
                                <button
                                    type="button"
                                    className="block w-full text-left px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-300"
                                    onClick={handleDirectAdminChat}
                                >
                                    Admin Chat
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="block w-full text-left px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-300"
                            >
                                Logout
                            </button>
                        </>
                    )}

                    {/* Improved dark mode toggle for mobile */}
                    <button
                        type="button"
                        onClick={handleSmoothDarkModeToggle}
                        className="w-full text-left px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300 focus:outline-none"
                    >
                        {darkMode ? "Switch to Light Mode ☀️" : "Switch to Dark Mode 🌙"}
                    </button>
                </div>
            </div>
        </nav>
    );
};

export default NavBar;